import { availableLanguages, loadLanguage } from "../lib/languages.mjs";
import { addExposure, characterCounts } from "../lib/exposure.mjs";
import { createPlayer } from "../lib/player.mjs";
import { ensureExposure, getLanguageProgress, loadProgress, migrateProgress, saveProgress } from "../lib/progress.mjs";
import { contentHash, routeHash } from "../lib/routing.mjs";
import { resolveContentPart, type ContentPart } from "../lib/catalog";
import type { AppState, Counts, LanguagePack, Page, Player, PlayerState, Program, Progress, Selection, Word } from "../types";

/** Audio clips are replaced in place on deploy; bumping this keeps browsers from replaying stale copies. */
export const AUDIO_VERSION = "20261010-main-sync";
const versioned = (src: string) => src + (src.includes("?") ? "&" : "?") + "v=" + AUDIO_VERSION;

export const SAVE_READ_WARNING = "Saved progress could not be read. Your original data has been kept; this session will not overwrite it.";
export const SAVE_WRITE_WARNING = "Your browser could not save this update. Keep this page open to retain your session.";

export interface Focus { ids: string[]; label: string }
export type Detail = { type: "word" | "character"; id: string } | null;

/** Everything a page needs for one loaded language. Created once per pack by `AppStore.contextFor`. */
export interface LanguageContext extends LanguagePack {
  /** The whole language; `deck`, `lessons`, `stories` and `programs` above are scoped to `content` when one is selected. */
  fullPack: LanguagePack;
  selection: Selection | null;
  content: ContentPart | null;
  progress: Progress;
  player: Player;
  save(): void;
  focus: Focus | null;
  href(page?: Page, itemId?: string, selection?: Selection | null): string;
  activity(page: Page, itemId: string | null | undefined, label: string): void;
  navigate(page: Page, itemId?: string): void;
  studyWords(ids: string[], label: string): void;
  reviewWord(word: Word): void;
  playWord(word: Word, phrase: boolean, extra?: Partial<Program>): Promise<void>;
  playProgram(program: Program | undefined, opts?: { restart?: boolean }): Promise<void>;
  openWord(id: string): void;
  openCharacter(char: string): void;
}

type AudioCompleteListener = (program: Program, context: { languageId: string; program: Program; sessionId: string }) => void;

/**
 * App-wide mutable state (progress, audio player, loaded language packs) kept outside React.
 * Components subscribe with `useSyncExternalStore`; `revision` changes whenever they should re-render.
 */
export class AppStore {
  readonly registry = availableLanguages() as { id: string; name: string; nativeName: string }[];
  state: AppState;
  saveWarning: string | null = null;
  playerState: PlayerState;
  focus: Focus | null = null;
  detail: Detail = null;
  /** Bumped to remount the current page (e.g. re-focusing study on the same URL). */
  routeNonce = 0;
  revision = 0;
  readonly player: Player;

  private storageBlocked = false;
  private listeners = new Set<() => void>();
  private audioComplete = new Set<AudioCompleteListener>();
  private packs = new Map<string, Promise<LanguagePack>>();
  private contexts = new Map<string, LanguageContext>();
  private lastSelection = new Map<string, string>();

  constructor(private storage: Storage | null = safeStorage(), private onPersist?: (state: AppState) => void) {
    try {
      if (!storage) throw new Error("no storage");
      this.state = loadProgress(storage) as AppState;
    } catch {
      this.state = { version: 2, selectedLanguageId: null, languages: {} };
      this.storageBlocked = true;
      this.saveWarning = SAVE_READ_WARNING;
    }

    const audio = document.createElement("audio");
    audio.preload = "metadata";
    audio.hidden = true;
    document.body.append(audio);
    this.player = (createPlayer as (options: unknown) => unknown)({
      audioElement: audio,
      onChange: (ps: PlayerState) => { this.playerState = ps; this.notify(); },
      onProgress: (snapshot: any, context: any) => this.onProgress(snapshot, context),
      onCue: (cue: any, context: any) => this.onCue(cue, context),
      onComplete: (program: Program, context: any) => this.onComplete(program, context),
    }) as Player;
    this.playerState = this.player.getState();
  }

  // ---- subscription -------------------------------------------------------
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  getRevision = () => this.revision;
  notify() { this.revision++; this.listeners.forEach((l) => l()); }
  onAudioComplete(listener: AudioCompleteListener) { this.audioComplete.add(listener); return () => { this.audioComplete.delete(listener); }; }

  // ---- persistence --------------------------------------------------------
  /** Write progress without re-rendering (player callbacks fire several times a second). */
  persist() {
    if (this.storageBlocked) { this.saveWarning = SAVE_READ_WARNING; return; }
    try { saveProgress(this.state, this.storage); } catch { this.saveWarning = SAVE_WRITE_WARNING; return; }
    this.onPersist?.(this.state);
  }
  save = () => { this.persist(); this.notify(); };

  /** Replace one language's progress with a copy from the server. Updates in place so open pages keep their reference. */
  applyRemote(languageId: string, data: unknown) {
    const next = (migrateProgress({ version: 2, selectedLanguageId: null, languages: { [languageId]: data } }) as AppState).languages[languageId] as Progress;
    const current = this.state.languages[languageId] as Record<string, unknown> | undefined;
    if (current) {
      for (const key of Object.keys(current)) delete current[key];
      Object.assign(current, next);
    } else this.state.languages[languageId] = next;
    this.persist();
    this.notify();
  }

  // ---- player callbacks (ported from the original app shell) --------------
  private onProgress(snapshot: any, context: any) {
    if (!context?.languageId || !context.program) return;
    const p = this.progressFor(context.languageId);
    if (!p.listeningSince) p.listeningSince = Date.now();
    p.listeningSeconds += Math.max(0, snapshot.listeningDelta || 0);
    const { listeningDelta: _delta, ...checkpoint } = snapshot;
    if (!context.program.practiceToken && !context.program.singleClip) {
      const previous = p.tracks.audio.programs[context.program.id];
      p.tracks.audio.programs[context.program.id] = {
        ...checkpoint, everCompleted: !!(previous?.everCompleted || previous?.completed || checkpoint.completed),
      };
    }
    this.persist();
  }

  private onCue(cue: any, context: any) {
    const p = this.progressFor(context.languageId);
    if (!p.exposure) return;
    if (cue.kind === "audio") {
      addExposure(p.exposure, {
        source: cue.source === "story" ? "story" : "study", kind: "audio",
        wordCounts: cue.wordCounts || {}, characterCounts: cue.characterCounts || {},
      });
      if (cue.storyId) {
        const st = (p.exposure.stories[cue.storyId] ||= { plays: 0, reads: 0, results: [], position: 0 });
        const cues = context.program.cues.filter((c: any) => c.kind === "audio" && c.storyId === cue.storyId);
        const credited = new Set(this.player.getState().snapshot?.creditedCueIds || []);
        const storyProgress = (p.tracks.stories[context.program.id + ":" + cue.storyId] ||= {});
        if (cues.length && cues.every((c: any) => credited.has(c.id)) && storyProgress.sessionId !== context.sessionId) {
          st.plays++; st.position = 0; storyProgress.sessionId = context.sessionId;
        } else st.position = Math.max(0, cues.findIndex((c: any) => !credited.has(c.id)));
      }
    }
    this.persist();
  }

  private onComplete(program: Program, context: any) {
    const p = this.progressFor(context.languageId);
    const checkpoint = p.tracks.audio.programs[program.id];
    if (checkpoint) { checkpoint.completed = true; checkpoint.everCompleted = true; }
    this.persist();
    this.audioComplete.forEach((listener) => listener(program, context));
    this.notify();
  }

  // ---- languages ----------------------------------------------------------
  progressFor(languageId: string): Progress { return getLanguageProgress(this.state, languageId) as Progress; }

  loadPack(id: string): Promise<LanguagePack> {
    let pending = this.packs.get(id);
    if (!pending) {
      pending = (loadLanguage(id, { fetcher: (url: URL | RequestInfo) => fetch(url, { cache: "no-cache" }) }) as Promise<any>)
        .then((p) => ({ ...p, programs: Array.isArray(p.programs) ? p.programs : p.programs.programs }) as LanguagePack)
        .catch((error) => { this.packs.delete(id); throw error; });
      this.packs.set(id, pending);
    }
    return pending;
  }

  /** Called once a pack has loaded and is about to be shown. */
  prepareLanguage(pack: LanguagePack) {
    const id = pack.definition.id;
    if (this.player.getState().languageId && this.player.getState().languageId !== id) { this.player.stop(); this.focus = null; }
    this.state.selectedLanguageId = id;
    const p = this.progressFor(id);
    ensureExposure(p, pack.deck, { characters: !!pack.definition.capabilities.characters });
    if (!p.listeningSince) p.listeningSince = Date.now();
    p.learnIndex = Math.min(Math.max(0, p.learnIndex), Math.max(0, pack.deck.length - 1));
    this.persist();
  }

  contextFor(pack: LanguagePack, selection: Selection | null = null): LanguageContext {
    const id = pack.definition.id;
    const selectionKey = JSON.stringify(selection ?? {});
    const key = id + "|" + selectionKey;
    const cached = this.contexts.get(key);
    if (cached && cached.fullPack === pack) return cached;
    // Moving to different content drops a "study these words" focus that belonged to the old content.
    if (this.lastSelection.has(id) && this.lastSelection.get(id) !== selectionKey) this.focus = null;
    this.lastSelection.set(id, selectionKey);
    const store = this;
    const p = this.progressFor(id);
    const characters = !!pack.definition.capabilities.characters;
    const content = selection ? resolveContentPart(pack, selection) : null;
    const ctx: LanguageContext = {
      ...pack,
      ...(content ? { deck: content.deck, lessons: content.lessons, stories: content.stories, programs: content.programs } : {}),
      fullPack: pack,
      selection,
      content,
      progress: p,
      player: this.player,
      save: this.save,
      get focus() { return store.focus; },
      href: (page = "part", itemId, sel = selection) =>
        page === "home" || page === "stats" ? routeHash(id, page, itemId) : contentHash(id, sel ?? {}, page, itemId),
      activity(page, itemId, label) {
        p.lastActivity = { page, itemId: itemId === "focus" ? null : itemId, label, ...selection, at: Date.now() };
        store.save();
      },
      navigate(page, itemId) { location.hash = ctx.href(page, itemId); },
      studyWords(ids, label) {
        store.focus = { ids, label };
        const next = ctx.href("study", "focus");
        if (location.hash === next) { store.routeNonce++; store.notify(); } else location.hash = next;
      },
      reviewWord(w) {
        addExposure(p.exposure, {
          source: "study", kind: "reading", wordCounts: { [w.id]: 1 },
          characterCounts: characters ? characterCounts(w.word) : {}, review: true,
        });
        store.save();
      },
      playWord(w, phrase, extra = {}) {
        const path = phrase ? w.phraseAudio : w.audio;
        const duration = pack.manifest[path]?.duration || 0;
        const program: Program = {
          id: "clip-" + w.id + "-" + (phrase ? "phrase" : "word"), title: phrase ? w.phrase : w.word, src: versioned(path), duration,
          trackId: "study", contentSelection: { ...selection }, singleClip: true, ...extra,
          cues: [{
            id: "clip", start: 0, end: duration, kind: "audio", source: "study", wordCounts: { [w.id]: 1 },
            characterCounts: (characters ? characterCounts(phrase ? w.phrase : w.word) : {}) as Counts,
          }],
        };
        return store.player.load(program, { languageId: id, autoplay: true });
      },
      async playProgram(program, { restart = false } = {}) {
        if (!program) return;
        const ps = store.player.getState();
        const same = ps.program?.id === program.id && ps.languageId === id;
        ctx.activity(program.trackId === "stories" ? "stories" : "audio", program.storyId || program.id, program.title);
        if (same) {
          if (restart || ps.snapshot?.completed) await store.player.restart(); else await store.player.play();
          return;
        }
        let resume = restart ? null : p.tracks.audio.programs[program.id];
        if (resume?.completed) resume = null;
        if (!restart && !resume && !p.tracks.audio.programs[program.id] && program.storyId && p.exposure.stories[program.storyId]?.position) {
          const cues = program.cues.filter((c) => c.kind === "audio");
          const index = Math.min(p.exposure.stories[program.storyId].position, cues.length - 1);
          const position = cues[index]?.start || 0;
          resume = {
            programId: program.id, position, playedRanges: position ? [[0, position]] : [],
            creditedCueIds: cues.slice(0, index).map((c) => c.id),
          };
        }
        await store.player.load({ ...program, src: versioned(program.src), contentSelection: { ...selection } }, { languageId: id, resume, autoplay: true });
        store.player.setRate(p.storyPrefs.speed || 1);
      },
      openWord(wordId) { store.detail = { type: "word", id: wordId }; store.notify(); },
      openCharacter(char) { store.detail = { type: "character", id: char }; store.notify(); },
    };
    this.contexts.set(key, ctx);
    return ctx;
  }

  closeDetail() { if (this.detail) { this.detail = null; this.notify(); } }
}

export function safeStorage(): Storage | null {
  try { return localStorage; } catch { return null; }
}
